import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useParams, useHistory } from 'react-router-dom';
import { GlobalFooter } from '@nexus/fff-global-footer';
import FeatureList from '@nexus/fff-ui-shared/FeatureList';
import map from 'lodash/map';
import { Helmet } from 'react-helmet';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useDecision } from '@optimizely/react-sdk';

import GlobalNav from 'shared-components/GlobalNav/GlobalNav';
import { PARIS_HILTON_SKU } from 'utils/parisHiltonConstants';
import { VIEW_SIZE } from 'utils/device';
import { trackOptimizelyExperiment, getAnonymousId } from 'utils/segment';

import {
  getProductBySlug,
  getProductById,
  getProductBySku,
  getRatingsById,
  getRatingGraphDetails,
  getSubscribableProducts,
} from 'services/api/products';
import {
  requestProductHasReviewed,
  receiveProductHasReviewed,
} from 'services/actions/productsActions';
import { getSubscriptionPlans } from 'services/api/localization';
import { requestAddToPublicCart } from 'services/actions/cartActions';

import { getUUID } from 'utils/user';
import { PH_FEATURE_LIST_BEIGE } from 'utils/parisHiltonConstants';

import {
  getIsExperimentRunning,
  PDP_REDESIGN_DECISIONS,
} from 'HOC/withOptimizely/withOptimizelyFF';
import withViewSize from 'HOC/withViewSize/withViewSize';
import useInView from 'hooks/useInView';
import ProductSummary from './ProductSummary/ProductSummary';
import ProductDetails from './ProductDetails';
import RatingsAndReviews from './RatingsAndReviews/RatingsAndReviews';
import FloatingCTA from './FloatingCTA/FloatingCTA';
import styles from './ProductDetail.module.css';

const initialGraphState = {
  averageRating: 0,
  totalReviewCount: 0,
  ratingDistribution: {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  },
};

const DECISION_NAMES = {
  productPDPRedesign: 'intro-product-pdp-redesign',
};

const formatProductData = (productData, subscribableProducts) => {
  const { defaultVariant, productCatalogVariants } = productData;
  return {
    ...productData,
    defaultVariantId: defaultVariant && defaultVariant.defaultVariantId,
    variants: map(productCatalogVariants, (variant) => {
      const { id, imageUrl, galleryUrls, variantDescriptors } = variant;
      const subscription = subscribableProducts?.[id];
      const isSubscribableShopVariant = !!subscription;
      return {
        ...variant,
        galleryUrls: [imageUrl, ...(galleryUrls || [])],
        variantDescriptors: variantDescriptors || [],
        subscription: subscription || {},
        isSubscribableShopVariant,
        isSubscriptionAvailable:
          isSubscribableShopVariant && !subscription.soldOut,
      };
    }),
  };
};

const createSchemaData = (product, campaignProductData, rating) => ({
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: `${product.productName}`,
  image: `${product.defaultVariant?.imageUrl}`,
  description: `${product.description || product.shortDescription}`,
  offers: {
    '@type': 'Offer',
    price: product.defaultVariant?.salePrice,
    priceCurrency: 'USD',
    // campaign product data is only available if the product is tied to an active campaign.
    ...(campaignProductData && {
      availability: campaignProductData.soldOut ? 'Sold out' : 'In stock',
    }),
    seller: {
      '@type': 'Organization',
      name: 'Fabfitfun',
    },
  },
  brand: {
    '@type': 'Brand',
    name: `${product.brandName}`,
  },
  ...(rating?.totalReviewCount > 0 && {
    review: {
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: 'FabFitFun customers',
      },
      datePublished: '',
      description: '',
      name: product.productName,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: rating?.averageRating,
        bestRating: 5,
      },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: rating?.averageRating,
      bestRating: 5,
      worstRating: 0,
      reviewCount: rating?.totalReviewCount,
    },
  }),
});

const formatCampaignProductData = (response) => {
  const { subscribable, percentOff, defaultVariantId, variants, campaignId } =
    response?.data?.items?.[0];
  const productData = variants[defaultVariantId];
  const { salePrice, price, sku, soldOut } = productData;
  return {
    salePrice,
    price,
    subscribable,
    percentOff,
    sku,
    soldOut,
    campaignId,
  };
};

const ProductDetailPage = ({
  userId,
  viewSize,
  userPlanCode,
  reviewedProducts,
  variantIdsBeingUpdated,
  getHasProductReviewed,
  updateReviewedProducts,
  addToPublicCart,
}) => {
  const ldClient = useLDClient();
  const [product, setProduct] = useState(null);
  const [campaignProductData, setCampaignProductData] = useState(null);
  const [rating, setRating] = useState({
    averageRating: 0,
    totalReviewCount: 0,
  });
  const [ratingGraph, setRatingGraph] = useState(initialGraphState);
  const [pageTitle, setPageTitle] = useState('FabFitFun');
  const [planPrice, setPlanPrice] = useState(0);
  const [productPDPRedesignData, setProductPDPRedesignData] = useState();
  const pdpRatingsHeaderRef = useRef(null);
  const { productSlug } = useParams();
  const history = useHistory();
  const [productPDPRedesign, isProductPDPRedesignReady] = useDecision(
    DECISION_NAMES.productPDPRedesign,
    {
      autoUpdate: true,
    },
    {
      overrideUserId: userId || getAnonymousId(),
    }
  );
  const {
    pdpIntroBundleSoftLaunch: pdpIntroBundleSoftLaunchFF,
    disclaimerPriceIncrease: disclaimerPriceIncreaseFF,
    rateAndReview: rateAndReviewFF,
    publicSubscriptions: publicSubscriptionsFF,
  } = useFlags();
  const { isInView: ctaIsInView, elementRef: ctaElementRef } = useInView();
  const { isInView: footerIsInView, elementRef: footerElementRef } =
    useInView();

  const loggedIn = !!userId;
  const hasReviews = rating.totalReviewCount > 0;
  const pdpRedesignExperimentRunning =
    getIsExperimentRunning(productPDPRedesign);
  const showPdpRedesign =
    pdpRedesignExperimentRunning &&
    productPDPRedesign.variationKey === PDP_REDESIGN_DECISIONS.redesign1;
  const isMobile = viewSize === VIEW_SIZE.small;
  const showFloatingCTA =
    !ctaIsInView && !footerIsInView && isMobile && !loggedIn;
  const hasBeenReviewed = reviewedProducts[product?.productId];
  const isParisHiltonKit = product?.defaultVariant.sku === PARIS_HILTON_SKU;

  useEffect(() => {
    const getRefillableProducts = async () => {
      if (!loggedIn && publicSubscriptionsFF) {
        const response = getSubscribableProducts();
        return response;
      }
      return Promise.resolve({});
    };
    const getProductData = async () => {
      try {
        const getProduct = isNaN(productSlug)
          ? getProductBySlug
          : getProductById;
        const [productResp, subscribableProductsResp] = await Promise.all([
          getProduct(productSlug),
          getRefillableProducts(),
        ]);
        const formattedProduct = formatProductData(
          productResp,
          subscribableProductsResp
        );
        setProduct(formattedProduct);
      } catch (e) {
        history.push('oops/404');
      }
    };
    getProductData();
  }, []);

  // Fetch product rating info
  useEffect(() => {
    const getProductRating = async () => {
      try {
        const ratings = await getRatingsById([product.productId]);
        setRating(ratings[product.productId]);
      } catch (err) {
        console.error(err);
      }
    };

    if (product && product.productId) {
      getProductRating();
    }
  }, [product]);

  // Fetch product rating graph info
  // Only fire call if `rating.totalReviewCount > 0`
  // Otherwise just show Empty reviews
  useEffect(() => {
    const getProductRatingGraph = async () => {
      try {
        const graphData = await getRatingGraphDetails(product.productId);
        setRatingGraph(graphData);
      } catch (err) {
        console.error(err);
      }
    };

    if (
      product &&
      product.productId &&
      rating &&
      rating.totalReviewCount &&
      rating.totalReviewCount > 0
    ) {
      getProductRatingGraph();
    }
  }, [product, rating]);

  useEffect(() => {
    if (product?.productId && loggedIn) {
      getHasProductReviewed(product.productId);
    }
  }, [product]);

  useEffect(() => {
    const getCampaignProductData = async () => {
      try {
        const {
          defaultVariant: { sku },
        } = product;
        const campaignProductResponse = await getProductBySku(sku);
        setCampaignProductData(
          formatCampaignProductData(campaignProductResponse)
        );
      } catch (err) {
        console.error(err);
      }
    };
    if (product) {
      getCampaignProductData();
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      const { productName, brandName } = product;
      const productTitle = brandName
        ? `${productName} by ${brandName}`
        : productName;
      setPageTitle(`${productTitle} - FabFitFun`);
    }
  }, [product]);

  useEffect(() => {
    if (pdpIntroBundleSoftLaunchFF) {
      const uuid = getUUID();
      // we want to console.log uuid here to make it easier for non-technical people to give us their UUID
      // we will use this UUID to determine if the user should see intro product functionality or not
      // eslint-disable-next-line
      console.log(uuid, 'LaunchDarkly user key');
      ldClient.identify({ key: uuid });
    }
  }, [pdpIntroBundleSoftLaunchFF]);

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        const data = await getSubscriptionPlans();
        setPlanPrice(data?.fffvip.price.amount);
      } catch (e) {
        console.error(e);
      }
    };
    if (disclaimerPriceIncreaseFF) {
      setPlanPrice(59.99);
    } else {
      fetchSubscriptionPlans();
    }
  }, [disclaimerPriceIncreaseFF]);

  useEffect(() => {
    if (isProductPDPRedesignReady) {
      setProductPDPRedesignData({
        enabled: productPDPRedesign.enabled,
        experimentName: productPDPRedesign.ruleKey,
        variationName: productPDPRedesign.variationKey,
        featureFlagName: productPDPRedesign.flagKey,
        experimentRunning: pdpRedesignExperimentRunning,
      });
    }
  }, [
    isProductPDPRedesignReady,
    pdpRedesignExperimentRunning,
    showPdpRedesign,
  ]);

  useEffect(() => {
    if (productPDPRedesignData) {
      trackOptimizelyExperiment(
        productPDPRedesignData,
        userId,
        campaignProductData?.campaignId,
        userPlanCode
      );
    }
  }, [productPDPRedesignData]);

  if (!product) return null;

  return (
    <div className={viewSize}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={product.description || product.shortDescription}
        />
        <meta
          property="og:title"
          content={`${product.productName} by ${product.brandName} - FabFitFun`}
        />
        <meta name="keywords" content="fabfitfun" />
        <meta charset="UTF-8" />
        <meta
          property="og:description"
          content={product.description || product.shortDescription}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="FabFitFun" />
        <meta
          property="og:url"
          content={`https://fabfitfun.com/product/${product.slug}`}
        />
        <meta
          property="fb:app_id"
          content={process.env.REACT_APP_FACEBOOK_APP_ID}
        />
        <meta property="og:image" content={product.defaultVariant?.imageUrl} />
        <meta property="twitter:card" content={product.shortDescription} />
        <meta property="twitter:creator" content="fabfitfun" />
        <meta
          property="twitter:title"
          content={`${product.productName} by ${product.brandName} - FabFitFun`}
        />
        <meta
          property="twitter:description"
          content={product.description || product.shortDescription}
        />
        <meta
          property="twitter:image"
          content={product.defaultVariant?.imageUrl}
        />
        <link
          rel="canonical"
          href={`https://fabfitfun.com/product/${product.slug}`}
        />
        <script type="application/ld+json">
          {JSON.stringify(
            createSchemaData(product, campaignProductData, rating)
          )}
        </script>
      </Helmet>
      <GlobalNav />
      {showPdpRedesign && (
        <div className={styles.productRedesign}>
          <p>LIMITED SUPPLY! {campaignProductData?.percentOff}% OFF.</p>
        </div>
      )}
      <div className={styles.container}>
        <ProductSummary
          productName={product.productName}
          productRatingData={rating}
          brandName={product.brandName}
          variants={product.variants}
          loggedIn={loggedIn}
          viewSize={viewSize}
          campaignProductData={campaignProductData}
          rateAndReviewFF={rateAndReviewFF}
          planPrice={planPrice}
          shippingRestrictions={product.shippingRestrictions}
          ctaElementRef={ctaElementRef}
          hasProductReviewed={hasBeenReviewed}
          reviewsSectionRef={pdpRatingsHeaderRef.current}
          isParisHiltonKit={isParisHiltonKit}
          variantIdsBeingUpdated={variantIdsBeingUpdated}
          addToPublicCart={addToPublicCart}
        />
        <ProductDetails
          layout="medium"
          shortDescription={product.shortDescription}
          includes={product.includes}
          features={product.features}
          color={product.color}
          design={product.design}
          size={product.size}
          dimensions={product.dimensions}
          howToUse={product.howToUse}
          ingredients={product.ingredients}
          materials={product.materials}
          techCompatibility={product.techCompatibility}
          careInstructions={product.careInstructions}
          pao={product.pao}
          additionalInfo={product.additionalInfo}
          countryOfOrigin={product.countryOfOrigin}
          dropshipDisclaimer={product.dropshipDisclaimer}
          paoDisclaimer={product.paoDisclaimer}
          cbdDisclaimer={product.cbdDisclaimer}
          isParisHiltonKit={isParisHiltonKit}
          loggedIn={loggedIn}
        />
        {!loggedIn && (
          <FeatureList
            title="A Nice Deal for Members and Brands"
            subtitle="Guaranteeing that our members get the best deals out there."
            list={PH_FEATURE_LIST_BEIGE}
            variation="icons"
          />
        )}
        {rateAndReviewFF && (
          <RatingsAndReviews
            ratingGraph={ratingGraph}
            productId={product.productId}
            productBrand={product.brandName}
            productName={product.productName}
            loggedIn={loggedIn}
            productHasReviewed={hasBeenReviewed}
            hasReviews={hasReviews}
            sku={product.defaultVariant.sku}
            imgUrl={product.defaultVariant.imageUrl}
            updateReviewedProducts={updateReviewedProducts}
            ref={pdpRatingsHeaderRef}
          />
        )}
      </div>
      {showFloatingCTA && (
        <FloatingCTA
          variants={product.variants}
          campaignProductData={campaignProductData}
          isParisHiltonKit={isParisHiltonKit}
          productName={product.productName}
          variantIdsBeingUpdated={variantIdsBeingUpdated}
          addToPublicCart={addToPublicCart}
        />
      )}
      <div ref={footerElementRef}>
        <GlobalFooter hideChatWidget={!loggedIn && isMobile} />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userId: state.userState.userId,
  viewSize: state.viewState.viewSize,
  userPlanCode: state.userState.planCode,
  reviewedProducts: state.productsState.reviewedProducts,
  variantIdsBeingUpdated: state.cartState.variantIdsBeingUpdated,
});

const mapDispatchToProps = {
  getHasProductReviewed: requestProductHasReviewed,
  updateReviewedProducts: receiveProductHasReviewed,
  addToPublicCart: requestAddToPublicCart,
};

ProductDetailPage.propTypes = {
  userId: PropTypes.number,
  viewSize: PropTypes.string.isRequired,
  userPlanCode: PropTypes.string.isRequired,
  reviewedProducts: PropTypes.shape({
    [PropTypes.number]: PropTypes.bool,
  }).isRequired,
  variantIdsBeingUpdated: PropTypes.shape({}).isRequired,
  getHasProductReviewed: PropTypes.func.isRequired,
  updateReviewedProducts: PropTypes.func.isRequired,
  addToPublicCart: PropTypes.func.isRequired,
};

ProductDetailPage.defaultProps = {
  userId: null,
};

export default compose(
  withViewSize,
  connect(mapStateToProps, mapDispatchToProps)
)(ProductDetailPage);
